/**
 * Hartenjagen explained, for someone who has never played it.
 *
 * Its own page rather than a section on the boerenbridge one: the two games
 * share a deck and nothing else, and the single thing a new player must
 * understand — that a *low* score wins — is exactly the opposite of the other.
 */

import type { ReactNode } from 'react';
import { Panel, Screen } from '@/components/ui';

export default function HeartsRules() {
  return (
    <Screen title="Hartenjagen" back="/regels" subtitle="Met z'n vieren · de laagste score wint">
      <Panel title="Waar het om gaat">
        <Prose>
          <Callout>
            Anders dan bij boerenbridge wil je hier juist <strong>zo min mogelijk</strong> punten.
            Punten zijn strafpunten.
          </Callout>
          <p>
            Er gaan <strong>twee spellen</strong> onder deze naam schuil, en welke jullie spelen
            kies je bij het aanmaken van een potje. Ze verschillen in het deck, in wat een kaart
            kost, en of er kaarten worden doorgegeven.
          </p>
        </Prose>
      </Panel>

      <Panel title="Wat een kaart kost">
        <Prose>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-slate-400">
                <th className="py-2 font-medium">&nbsp;</th>
                <th className="py-2 font-medium">Nederlands</th>
                <th className="py-2 font-medium">Amerikaans</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              <tr className="border-b border-white/5">
                <td className="py-2">Kaarten in het spel</td>
                <td>32 (7 t/m aas)</td>
                <td>52</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2">Elke ♥ harten</td>
                <td>1</td>
                <td>1</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2">♠ Schoppenvrouw</td>
                <td>5</td>
                <td>13</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2">♣ Klaverenboer</td>
                <td>2</td>
                <td>telt niet</td>
              </tr>
              <tr className="border-b border-white/5 font-semibold">
                <td className="py-2">Samen per ronde</td>
                <td>15</td>
                <td>26</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2">Kaarten doorgeven</td>
                <td>nee</td>
                <td>drie, elke ronde een andere kant op</td>
              </tr>
              <tr>
                <td className="py-2">Wie komt uit</td>
                <td>♣ 7</td>
                <td>♣ 2</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-3">
            Tel het maar na: 8 harten van 1 plus de vrouw van 5 plus de boer van 2 is precies 15 in
            het Nederlandse spel, en 13 harten plus een vrouw van 13 is 26 in het Amerikaanse.
            Tijdens het spelen staat midden op tafel hoeveel er nog rondgaan —{' '}
            <em>nog 14 van de 26</em> betekent dat de schoppenvrouw nog niet gevallen is.
          </p>
          <p>
            Wie het eerst boven de 100 komt, beëindigt het spel — en dan wint degene met de mínste
            punten. Staan er twee of drie gelijk, dan delen ze die plaats.
          </p>
          <p>
            Er is <strong>geen troef</strong>. De hoogste kaart van de gevraagde kleur pakt de slag,
            en wie de slag pakt mag uitkomen.
          </p>
        </Prose>
      </Panel>

      <Panel title="1 · Drie kaarten doorgeven (alleen Amerikaans)">
        <Prose>
          <p>
            Iedereen krijgt 13 kaarten en kiest er drie om door te geven. De richting wisselt per
            ronde: eerst naar links, dan naar rechts, dan naar de overkant — en de vierde ronde geef
            je <strong>niets</strong> door.
          </p>
          <Callout>
            Iedereen kiest tegelijk en niemand ziet wat er aankomt. Je geeft dus weg zonder te weten
            wat je terugkrijgt.
          </Callout>
          <p>
            De schoppenvrouw wegdoen is de klassieker. Een kleur helemaal leegmaken is de andere:
            daarna kun je die kleur niet meer bekennen en mag je er van alles op gooien.
          </p>
        </Prose>
      </Panel>

      <Panel title="2 · Spelen">
        <Prose>
          <p>
            Wie de <strong>klaveren 2</strong> heeft, komt uit. Je moet altijd de gevraagde kleur
            bekennen als je hem hebt. Heb je hem niet, dan mag je alles gooien — en dat is precies
            het moment om iemand de schoppenvrouw te bezorgen.
          </p>
          <p>Twee regels beschermen het begin van de ronde:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              In de <strong>eerste slag</strong> mag niemand harten of de schoppenvrouw weggooien.
            </li>
            <li>
              <strong>Harten uitkomen</strong> mag pas als er al eens een harten (of de
              schoppenvrouw) gevallen is. Dat heet "harten zijn gebroken".
            </li>
          </ul>
          <p className="text-slate-400">
            De app zet kaarten die je nu niet mag spelen grijs. Tik er gerust op — hij vertelt dan
            waarom niet.
          </p>
        </Prose>
      </Panel>

      <Panel title="3 · De maan schieten">
        <Prose>
          <Callout>
            Pak je in één ronde <strong>alle 26 punten</strong> — alle harten én de schoppenvrouw —
            dan krijg jij er nul en alle anderen 26.
          </Callout>
          <p>
            Dat is de reden om goed op te letten als iemand alle slagen lijkt te pakken: één harten
            aan die persoon níet gunnen kan een ronde helemaal omdraaien. Het is ook de reden dat
            een hopeloze hand nog te redden is — soms is doorpakken beter dan schade beperken.
          </p>
        </Prose>
      </Panel>

      <Panel title="Veelgemaakte fouten">
        <Prose>
          <ul className="list-disc space-y-1 pl-5">
            <li>Denken dat hoog goed is. Laag wint.</li>
            <li>
              Je hoge schoppen (heer, aas) houden terwijl je de vrouw niet hebt — je pakt haar dan
              zelf zodra iemand schoppen speelt.
            </li>
            <li>
              Alle kleine kaarten weggeven bij het doorgeven. Met alleen hoge kaarten pak je elke
              slag die je niet wilt.
            </li>
          </ul>
        </Prose>
      </Panel>
    </Screen>
  );
}

function Prose({ children }: { children: ReactNode }) {
  return <div className="space-y-3 text-sm leading-relaxed text-slate-300">{children}</div>;
}

function Callout({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border-l-2 border-accent-500 bg-accent-500/10 px-3 py-2 text-slate-200">
      {children}
    </p>
  );
}
